import { useCallback, useEffect, useRef, useState } from 'react';
import { sprekersApi } from '../api';
import useSessionEvents from './useSessionEvents';
import type { SessionEvent } from '../types';

export type LiveStreamStatus =
  | 'minting'
  | 'connecting'
  | 'open'
  | 'reconnecting'
  // Automatisch herstellen is opgegeven (netwerk weg, of de watchdog/resync-
  // grens is bereikt). De sessie zelf kan nog leven: de pagina toont de laatst
  // bekende slide en biedt een handmatige "opnieuw verbinden" (retryKey).
  | 'disconnected'
  // De server zegt dat de sessie niet meer bestaat of beëindigd/verlopen is
  // (bijv. beëindigd tijdens een verbindingsonderbreking).
  | 'ended'
  | 'unrecoverable';

export interface LiveSessionStreamOptions {
  needsResync?: (event: SessionEvent) => boolean;
  /** Verhoog om na 'disconnected' handmatig opnieuw te beginnen. */
  retryKey?: number;
}

type TerminalState = 'ended' | 'disconnected' | 'unrecoverable' | null;

/**
 * Combineert het minten van een sessiegebonden stream-token
 * (backend/sprekers/service.py create_stream_token) met de SSE-verbinding
 * zelf (useSessionEvents), inclusief precies één stille herstelpoging
 * (nieuw token + nieuwe verbinding) als de stream definitief wordt
 * afgewezen (bijv. een verlopen token, sectie 33). Dit is geen "agressieve
 * reconnect-loop" bovenop de ingebouwde EventSource-reconnect (die lost
 * alleen tijdelijke onderbrekingen zelf op) -- een afgewezen token kan de
 * browser nooit zelf herstellen, dus is één vers token proberen de enige
 * manier om ooit weer verbinding te krijgen. De herstelpoging wordt opnieuw
 * toegestaan zodra de verbinding weer 'open' is geweest, zodat een lange
 * dienst (tokenvervaldatum, meerdere onderbrekingen) niet na de eerste keer
 * zonder herstel zit; mislukkingen blijven begrensd.
 *
 * Mislukt het minten zelf, dan hangt de uitkomst af van de reden:
 * - HTTP 400/404: de sessie is beëindigd, verlopen of verdwenen -> 'ended'
 *   (duidelijk "sessie beëindigd" i.p.v. een algemene fout);
 * - geen respons (netwerk weg): 'disconnected' -> handmatig opnieuw proberen;
 * - elke andere fout (bijv. 401/403, geen toegang meer): 'unrecoverable'.
 */
export default function useLiveSessionStream(
  sessionId: string | null | undefined,
  onEvent: (event: SessionEvent) => void,
  options: LiveSessionStreamOptions = {},
): LiveStreamStatus {
  const retryKey = options.retryKey ?? 0;
  const [streamToken, setStreamToken] = useState<string | null>(null);
  const [terminal, setTerminal] = useState<TerminalState>(null);
  const recoveryAttemptedRef = useRef(false);

  const mint = useCallback(async () => {
    if (!sessionId) return;
    try {
      const issued = await sprekersApi.createStreamToken(sessionId);
      setStreamToken(issued.stream_token);
    } catch (error: any) {
      setStreamToken(null);
      const httpStatus = error?.response?.status;
      if (httpStatus === 400 || httpStatus === 404) setTerminal('ended');
      else if (!error?.response) setTerminal('disconnected');
      else setTerminal('unrecoverable');
    }
  }, [sessionId]);

  useEffect(() => {
    recoveryAttemptedRef.current = false;
    setTerminal(null);
    setStreamToken(null);
    if (sessionId) mint();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, retryKey]);

  // Een handmatige retry (retryKey) mint hierboven een vers token; dat nieuwe
  // token zet de verbinding opnieuw op, dus retryKey hoeft hier niet door.
  const sseStatus = useSessionEvents(sessionId, streamToken, onEvent, { needsResync: options.needsResync });

  useEffect(() => {
    if (sseStatus === 'open') {
      recoveryAttemptedRef.current = false;
      return;
    }
    if (sseStatus !== 'error') return;
    if (recoveryAttemptedRef.current) {
      setTerminal('unrecoverable');
      return;
    }
    recoveryAttemptedRef.current = true;
    mint();
  }, [sseStatus, mint]);

  if (terminal) return terminal;
  if (!streamToken) return 'minting';
  if (sseStatus === 'open') return 'open';
  if (sseStatus === 'reconnecting') return 'reconnecting';
  if (sseStatus === 'stopped') return 'disconnected';
  return 'connecting';
}
